// Saving a file to the user's downloads, from a blob we already hold in memory.
//
// Everything the app offers as a file goes through here or an equivalent: the
// PDF exports (jsPDF's doc.save), the Excel exports (XLSX.writeFile) and the
// CSV helpers all hand the browser a named download rather than a tab. Stored
// documents used to be the exception — they opened a blob URL in a new tab, and
// a blob: URL carries no filename or extension, so the browser guessed: Word
// files downloaded as a side effect (leaving the opened tab on about:blank),
// PDFs rendered in the viewer under a uuid address whose Save dialog lost the
// .pdf type once the object URL was revoked. Downloading by name avoids all of
// that and needs no popup.

import axios from "axios";
import { API_BASE_URL } from "./config";

const DOCUMENTS_API = `${API_BASE_URL}/documents`;

/**
 * A record's name as a safe filename stem: "First aid bag 7 / Large" becomes
 * "First_aid_bag_7_Large". Falls back to `fallback` when there is nothing usable
 * left, so a download always has a name.
 */
export const fileSlug = (name, fallback = "download") =>
  (name || "").replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "") || fallback;

// Trigger a download of `blob` named `filename`. The anchor's `download`
// attribute is what makes the browser write the file out instead of trying to
// display it, and is the only place the name survives.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Let the download start before releasing the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Download a stored document ({ id, original_filename }).
 *
 * The document endpoint requires the bearer token, so a plain <a href> would
 * 401 — fetch the bytes via axios (token attached by the request interceptor)
 * and save them under the name they were uploaded with. Rejects if the fetch
 * fails; the axios interceptor has already surfaced a toast.
 */
export const downloadDocument = async (doc, urlBase = DOCUMENTS_API) => {
  const res = await axios.get(`${urlBase}/${doc.id}`, { responseType: "blob" });
  downloadBlob(res.data, doc.original_filename || `document-${doc.id}`);
};
